import { handleApiResult } from '@store/error';
import { apiSlice, toQueryParams } from './apiSlice';

/**
 * One offline credit grant: a seat session's allowance to keep working once it is out of contact
 * and its seat window has ended, and what it has reported using. No device name or id, by design.
 */
export interface OfflineGrant {
  grantId: string;
  licenseId: string;
  app: 'supar' | 'composer';
  sessionId: string;
  issuedAt: string;
  /** The last activation that granted or held a seat. */
  refreshedAt: string;
  /** The end of the week the device may work offline in. */
  expiresAt: string;
  windowSeconds: number;
  budgetSeconds: number;
  ticketSeconds: number;
  /**
   * The tickets filed and the time they stand for, the part beyond the limit likewise: each ticket
   * at the seconds it claims, held to the ticket length, as Activity and Seat usage count it. Never
   * the tickets times fifteen minutes, which disagreed with both for a client claiming part tickets.
   */
  reportedTickets: number;
  reportedSeconds: number;
  beyondCapacityTickets: number;
  beyondCapacitySeconds: number;
  duplicateTickets: number;
  anomalyCount: number;
  lastAnomaly?: { kind: string, detail?: string, at: string };
  lastReportedAt?: string;
  /** Not usage owed: builds before 11.10 cannot report, and most devices never need to. */
  neverReported: boolean;
  holdsSeat: boolean;
}

export interface OfflineGrantPage {
  items: OfflineGrant[];
  total: number;
  page: number;
  limit: number;
  totals: {
    grants: number;
    reportedTickets: number;
    reportedSeconds: number;
    beyondCapacityTickets: number;
    beyondCapacitySeconds: number;
    neverReported: number;
  };
}

export interface OfflineGrantQuery {
  licenseId?: string;
  reported?: 'all' | 'reported' | 'never';
  page?: number;
  limit?: number;
}

export const offlineUsageApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOfflineGrants: builder.query<OfflineGrantPage, OfflineGrantQuery>({
      query: (params) => ({ url: '/v1/offline-grants', params: toQueryParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const { useGetOfflineGrantsQuery } = offlineUsageApiSlice;
